import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.css";
import { Navigation, Pagination } from "swiper/modules";
import { Images } from "../../../utils/imageConstants";
import { useQuery } from "@tanstack/react-query";
import type { IMatchesResponse, ITournamentsByLeagueResponse } from "../types/home";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { useAppSelector } from "../../../redux/hooks";
import { selectSelectedLeagueId } from "../../../redux/slice/selectedLeagueSlice";
import { MdLocationOn } from "react-icons/md";
import {
  formatMatchVenueLine,
  formatTime,
  formatTournamentDateWindowShort,
  formatUpcomingMatchDayLabel,
  getTimeUntilMatch,
  resolvePublicImageUrl,
} from "../../../helper/helper";
import { useNavigate } from "react-router-dom";

type MatchesResponseWithMeta = IMatchesResponse & {
  __statusUsed?: "IN_PROGRESS" | "SCHEDULED" | "ENDED";
};

const STATUS_ORDER: Array<"IN_PROGRESS" | "SCHEDULED" | "ENDED"> = [
  "IN_PROGRESS",
  "SCHEDULED",
  "ENDED",
];

const MATCHES_PAGE_SIZE = 6;

async function resolveTournamentIdForLeague(leagueId: string): Promise<string | null> {
  const { data } = await Api.get<ITournamentsByLeagueResponse>(
    `${QUERY_KEY.getTournamentsByLeagues}/${leagueId}`
  );
  const list = data?.tournamentResponses ?? [];
  if (list.length === 0) return null;
  const fromSession = sessionStorage.getItem("tournamentId");
  if (fromSession && list.some((t) => t.tournamentId === fromSession)) {
    return fromSession;
  }
  return list[0]?.tournamentId ?? null;
}

async function fetchAllMatches(leagueId: string): Promise<MatchesResponseWithMeta> {
  const tournamentId = await resolveTournamentIdForLeague(leagueId);
  if (!tournamentId) {
    return { matchResponseList: [], __statusUsed: undefined } as MatchesResponseWithMeta;
  }

  for (const status of STATUS_ORDER) {
    const { data } = await Api.get<IMatchesResponse>(
      QUERY_KEY.matchByTournamentIdAndStatusPageable,
      { params: { status, page: 0, size: 100, tournamentId } }
    );
    const list = data?.matchResponseList ?? [];
    if (list.length > 0) {
      return { ...data, __statusUsed: status };
    }
  }
  return { matchResponseList: [], __statusUsed: undefined } as MatchesResponseWithMeta;
}

const MatchesWrapper: React.FC = () => {
  const navigate = useNavigate();
  const nextMatchRef = useRef<HTMLButtonElement>(null);
  const prevMatchRef = useRef<HTMLButtonElement>(null);
  const selectedLeagueId = useAppSelector(selectSelectedLeagueId);
  const [visibleCount, setVisibleCount] = useState(MATCHES_PAGE_SIZE);

  const {
    data: matches,
    isLoading,
    isError,
  } = useQuery<MatchesResponseWithMeta>({
    queryKey: [
      QUERY_KEY.matchByTournamentIdAndStatusPageable,
      "league-home-matches",
      selectedLeagueId,
      typeof window !== "undefined" ? sessionStorage.getItem("tournamentId") : null,
    ],
    enabled: !!selectedLeagueId,
    queryFn: () => fetchAllMatches(String(selectedLeagueId)),
    refetchInterval: (query) =>
      query.state.data?.__statusUsed === "IN_PROGRESS" ? 30_000 : false,
  });

  const allMatchesList = matches?.matchResponseList ?? [];
  const list = allMatchesList.slice(0, visibleCount);
  const hasMore = allMatchesList.length > visibleCount;

  const shouldRender = allMatchesList.length > 0;
  if (isLoading) return null;
  if (isError) return null;
  if (!shouldRender) return null;

  return (
    <>
      <div className="matches-wrapper">
        <h1 className="heading">
          {matches?.__statusUsed === "IN_PROGRESS"
            ? "Live Matches"
            : matches?.__statusUsed === "SCHEDULED"
              ? "Upcoming Matches"
              : "Completed Matches"}
        </h1>
        <div className="matches-carousel">
          <div className="custom-slick-slider">
            <button ref={prevMatchRef} className="custom-prev-arrow">
              <img src={Images.back_arrow} />
            </button>
            <button ref={nextMatchRef} className="custom-next-arrow">
              <img src={Images.card_arrow_image} />
            </button>
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={16}
              slidesPerView={3}
              breakpoints={{
                640: { slidesPerView: 2, spaceBetween: 16 },
                1024: { slidesPerView: 3, spaceBetween: 16 },
              }}
              navigation
              pagination={false}
              onInit={(swiper) => {
                // @ts-expect-error – Swiper expects HTMLElement
                swiper.params.navigation.prevEl = prevMatchRef.current;
                // @ts-expect-error – Swiper expects HTMLElement
                swiper.params.navigation.nextEl = nextMatchRef.current;
                swiper.navigation.init();
                swiper.navigation.update();
              }}
            >
              {list.map((match, index) => {
                const scheduleRaw =
                  match?.dateTime || match?.startDate || null;
                const tournamentWindowLine = formatTournamentDateWindowShort(match);
                const timeInfo = formatTime(scheduleRaw);
                const timeUntil = getTimeUntilMatch(scheduleRaw);
                const dayLabel = scheduleRaw
                  ? formatUpcomingMatchDayLabel(scheduleRaw)
                  : tournamentWindowLine
                    ? `Tournament: ${tournamentWindowLine}`
                    : "—";
                const displayClockTime = scheduleRaw ? timeInfo.time : "TBD";
                return (
                  <SwiperSlide key={index}>
                    <div className="live-matches-grid">
                      <div className="live-match-card" key={match?.matchId} onClick={() => navigate(`/matches-details/${match?.matchId}`)}>
                        <div className="live-match-content">
                          <div className="live-match-header">
                            <div className="live-indicator">
                              {matches?.__statusUsed === "IN_PROGRESS" ? (
                                <span className="live-tag">LIVE</span>
                              ) : matches?.__statusUsed === "SCHEDULED" ? (
                                <span className="upcoming-tag">UPCOMING</span>
                              ) : (
                                <span className="result-tag">RESULT</span>
                              )}
                              <div className="match-info">
                                <span className="tournament-name">
                                  {match?.tournament?.tournamentName ||
                                    match?.leagueName}
                                </span>
                                <span className="separator">|</span>
                                <span className="match-type">
                                  {match?.matchType ||
                                    match?.tournament?.matchType ||
                                    "—"}
                                </span>
                              </div>
                            </div>
                            <div className="divider-line"></div>
                            {matches?.__statusUsed === "SCHEDULED" ? (
                              <>
                                <div className="teams-and-time-section">
                                  <div className="teams-section">
                                    <div className="team-row">
                                      <div className="team-info">
                                        <div className="team-logo">
                                          <img
                                            src={resolvePublicImageUrl(match?.teamOne?.imageUrl) || Images.default_team_image}
                                            alt={match?.teamOne?.teamName || "Team Logo"}
                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.default_team_image; }}
                                          />
                                        </div>
                                        <span className="team-name">
                                          {match?.teamOne?.teamName}
                                        </span>
                                      </div>
                                      {/* <div className="team-score" aria-hidden>
                                        <span className="score">—</span>
                                      </div> */}
                                    </div>

                                    <div className="team-row">
                                      <div className="team-info">
                                        <div className="team-logo">
                                          <img
                                            src={resolvePublicImageUrl(match?.teamTwo?.imageUrl) || Images.default_team_image}
                                            alt={match?.teamTwo?.teamName || "Team Logo"}
                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.default_team_image; }}
                                          />
                                        </div>
                                        <span className="team-name">
                                          {match?.teamTwo?.teamName}
                                        </span>
                                      </div>
                                      {/* <div className="team-score" aria-hidden>
                                        <span className="score">—</span>
                                      </div> */}
                                    </div>
                                  </div>

                                  <div className="time-section">
                                    <div className="time-info">
                                      <span className="time-label">
                                        {dayLabel}
                                      </span>
                                      <span className="time-value">
                                        {displayClockTime}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="countdown-section">
                                  <span className="countdown-text">
                                    {timeUntil.isUnknown ? (
                                      tournamentWindowLine
                                        ? "Kickoff time not set for this fixture"
                                        : "Schedule TBD"
                                    ) : timeUntil.isPast ? (
                                      "Match time reached"
                                    ) : (
                                      <>
                                        Match starts in{" "}
                                        {timeUntil.days > 0 && (
                                          <>
                                            <span className="countdown-bold">
                                              {timeUntil.days}
                                            </span>{" "}
                                            days{" "}
                                          </>
                                        )}
                                        <span className="countdown-bold">
                                          {timeUntil.hours}
                                        </span>{" "}
                                        hrs{" "}
                                        <span className="countdown-bold">
                                          {timeUntil.minutes}
                                        </span>{" "}
                                        min
                                      </>
                                    )}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                {(() => {
                                  const scorecards = match?.inningScorecardList ?? [];
                                  const teamOneInning = scorecards.find(
                                    (i) => i.battingTeamName === match?.teamOne?.teamName
                                  );
                                  const teamTwoInning = scorecards.find(
                                    (i) => i.battingTeamName === match?.teamTwo?.teamName
                                  );

                                  const renderScore = (inning: typeof teamOneInning) => {
                                    if (!inning) return <span className="score">YET TO BAT</span>;
                                    return (
                                      <div className="score-with-overs">
                                        <span className="score">
                                          {inning.runs}/{inning.wicket}
                                        </span>
                                        <span className="overs">
                                          ({inning.over}.{inning.ball} ov)
                                        </span>
                                      </div>
                                    );
                                  };

                                  return (
                                    <div className="teams-section">
                                      <div className="team-row">
                                        <div className="team-info">
                                          <div className="team-logo">
                                            <img
                                              src={resolvePublicImageUrl(match?.teamOne?.imageUrl) || Images.default_team_image}
                                              alt={match?.teamOne?.teamName || "Team Logo"}
                                              onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.default_team_image; }}
                                            />
                                          </div>
                                          <span className="team-name">{match?.teamOne?.teamName}</span>
                                        </div>
                                        <div className="team-score">
                                          {renderScore(teamOneInning)}
                                        </div>
                                      </div>

                                      <div className="team-row">
                                        <div className="team-info">
                                          <div className="team-logo">
                                            <img
                                              src={resolvePublicImageUrl(match?.teamTwo?.imageUrl) || Images.default_team_image}
                                              alt={match?.teamTwo?.teamName || "Team Logo"}
                                              onError={(e) => { (e.currentTarget as HTMLImageElement).src = Images.default_team_image; }}
                                            />
                                          </div>
                                          <span className="team-name">{match?.teamTwo?.teamName}</span>
                                        </div>
                                        <div className="team-score">
                                          {renderScore(teamTwoInning)}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {matches?.__statusUsed === "ENDED" && (
                                  <span className="result-text">
                                    {match?.matchResult || "-"}
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <div className="match-details">
                            <p className="umpire-info">
                              <span className="label">Umpire : </span>
                              <span className="value">
                                {match?.umpire?.trim() ||
                                  match?.umpireName?.trim() ||
                                  "TBD"}
                              </span>
                            </p>
                            <div className="venue-info">
                              <div className="venue-icon">
                                <MdLocationOn size={16} color="#201E52" />
                              </div>
                              <span className="venue-name">
                                {formatMatchVenueLine(match)}
                              </span>
                            </div>
                          </div>

                          {/* <div className="divider-line"></div> */}

                          {/* <button 
                  className="match-center-btn"
                  onClick={() => navigate(`/matches-details/${match?.matchId}`)}
                >
                  <span>Match Center</span>
                  <div className="btn-icon">
                    <img src={scoreCradIcon} alt="Arrow" />
                  </div>
                </button> */}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
        {/* {hasMore && (
          <div className="show-more-wrapper">
            <button
              className="show-more-btn"
              onClick={() => setVisibleCount((c) => c + MATCHES_PAGE_SIZE)}
              disabled={isLoading}
            >
              Show More
            </button>
          </div>
        )} */}
      </div>
    </>
  );
};

export default MatchesWrapper;

/*<div className="matches-card">
                    <div className="match-card">
                      <div className="match-card-header">
                        <div className="left">
                          <p className="league">{match?.leagueName || "-"} • Supreme …</p>
                        </div>
                        <div className="live-badge">
                          <span className="live-dot" />
   {matches?.__statusUsed==="IN_PROGRESS" ? "Live" : matches?.__statusUsed === "SCHEDULED" ? "Upcoming" : "Completed"}
                          
                        </div>
                      </div>

                      <div className="match-card-body">
                        <div className="match-card-body-info">
                          <div className="list-title">OTHER LIMITED OVERS</div>

                          <div className="meta-row">
                            <span className="loc-pin"><img src={Images.blue_location_icon} /></span>
                            <span className="subtle">Wankhede Stadium</span>
                          </div>
                        </div>

                        <div className="teams">
                          <div className="team">
                            <div className="logo">
                              <img src={cardInfoImage} alt="Team One" />
                            </div>
                            <div className="name">{match?.teamOne?.teamName}</div>
                          </div>
                          <div className="score">
                            <div className="sub">(5.0/5.0)</div>
                            <span className="runs">71/8</span>
                          </div>
                          <div className="team">
                            <div className="logo">
                              <img src={Images.match_card_image} alt="Team Two" />
                            </div>
                            <div className="name">{match?.teamTwo?.teamName}</div>
                          </div>
                          <div className="score-yet">
                            <span className="yet">Yet To Bat</span>
                          </div>
                        </div>
                      </div>

                      <div className="divider" />
                      <div className="match-card-footer">
                        <div><span className="label">CRR :</span> 5.5</div>
                        <div><span className="label">RRR :</span> 1.6</div>
                      </div>
                      <div className="divider-two" />
                    </div>
                  </div>*/
